/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createSimpleEntityStore } from 'src/luis/core/stores/simpleEntityStore';
import { TreeItemData } from 'src/luis/modules/entities/types/treeItemData';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';

/**
 * Generate an array of items to feed into a list by traversing a tree and discarding children of collapsed nodes
 * @param entityStores List of entities.
 * @param collapsedEntities Which of the entities are currently collapsed (aka hiding children).
 * @param newChildParentId Used to track which entity is trying to add a child (to add a row below it).
 */
export const generateFlattenedEntities = (
    entityStores: readonly EntityStore[],
    collapsedEntities: Record<string, boolean>,
    newChildParentId: LuisId
) => {
    const newEntityItems: TreeItemData[] = [];

    const parseMLEntityNode = (rootEntityId: LuisId, parentEntityId: LuisId, entityStore: SimpleEntityStore, level: number) => {
        const entityId = <string>entityStore.id;
        const isEntityCollapsed = collapsedEntities[entityId];

        newEntityItems.push({
            rootEntityId,
            parentEntityId,
            entityStore,
            level,
            isCollapsed: isEntityCollapsed,
            kind: 'existingEntity'
        });

        if (!isEntityCollapsed) {
            entityStore.children.forEach(child => parseMLEntityNode(rootEntityId, entityId, child, level + 1));

            if (entityId === newChildParentId) {
                const newSimpleEntity = createSimpleEntityStore('', { name: '' });

                newEntityItems.push({
                    rootEntityId,
                    parentEntityId: entityId,
                    entityStore: newSimpleEntity,
                    level: level + 1,
                    isCollapsed: false,
                    kind: 'newEntity'
                });
            }
        }
    };

    entityStores.forEach(entityStore => {
        switch (entityStore.typeId) {
            case ModelType.Simple:
                parseMLEntityNode(entityStore.id, entityStore.id, <SimpleEntityStore>entityStore, 0);
                break;
            case ModelType.Hierarchical:
                newEntityItems.push({ rootEntityId: entityStore.id, entityStore, level: 0, kind: 'existingEntity' });
                break;
            default:
                newEntityItems.push({ rootEntityId: null, entityStore, level: 0, kind: 'existingEntity' });
        }
    });

    return newEntityItems;
};
